import { Badge } from 'src/design/atoms/Badge';
import { Code } from 'src/design/atoms/Code';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Drawer } from 'src/design/molecules/Drawer';
import { PropertyList } from 'src/design/molecules/PropertyList';
import { Timeline } from 'src/design/molecules/Timeline';
import React from 'react';
import { useBackgroundJob } from 'src/api/queries/admin/useBackgroundJobs';
import { formatDateTime } from 'src/utils';
import {
  formatJobDuration,
  jobStateColor,
} from 'src/routes/Settings/BackgroundJobs/jobActions';

export interface IBackgroundJobDetailDrawerProps {
  jobId: string | null;
  onClose: () => void;
}

export const BackgroundJobDetailDrawer: React.FC<
  IBackgroundJobDetailDrawerProps
> = ({ jobId, onClose }) => {
  const detailQuery = useBackgroundJob(jobId);
  const detail = detailQuery.data;
  const summary = detail?.summary;

  return (
    <Drawer
      opened={jobId !== null}
      onClose={onClose}
      title="Background Job"
      position="right"
      size="55%"
    >
      <Stack pos="relative" mih={200}>
        <LoadingOverlay visible={detailQuery.isFetching} />

        {summary && (
          <>
            <Group gap="xs">
              <Badge color={jobStateColor(summary.state)}>
                {summary.state}
              </Badge>
              <Text size="sm" fw={600}>
                {summary.jobName}
              </Text>
            </Group>

            <PropertyList
              size="xs"
              items={[
                { label: 'Job ID', value: summary.id },
                { label: 'Queue', value: summary.queue ?? '—' },
                {
                  label: 'Created',
                  value: formatDateTime(summary.createdAt) || '—',
                },
                {
                  label: 'Enqueued',
                  value: formatDateTime(summary.enqueuedAt) || '—',
                },
                {
                  label: 'Scheduled',
                  value: formatDateTime(summary.scheduledAt) || '—',
                },
                {
                  label: 'Started',
                  value: formatDateTime(summary.startedAt) || '—',
                },
                {
                  label: 'Succeeded',
                  value: formatDateTime(summary.succeededAt) || '—',
                },
                {
                  label: 'Failed',
                  value: formatDateTime(summary.failedAt) || '—',
                },
                {
                  label: 'Deleted',
                  value: formatDateTime(summary.deletedAt) || '—',
                },
                {
                  label: 'Duration',
                  value: formatJobDuration(summary.totalDurationMs),
                },
                { label: 'Exception', value: summary.exceptionType ?? '—' },
              ]}
            />

            {summary.exceptionMessage && (
              <Code block>{summary.exceptionMessage}</Code>
            )}
          </>
        )}

        {detail && (
          <>
            <Title order={6}>Arguments</Title>
            {detail.arguments.length === 0 ? (
              <Text c="dimmed" size="xs">
                No arguments.
              </Text>
            ) : (
              <Stack gap={4}>
                {detail.arguments.map((argument, index) => (
                  <Code key={`${index}-${argument}`} block>
                    {argument}
                  </Code>
                ))}
              </Stack>
            )}

            <Title order={6}>State History</Title>
            <Timeline bulletSize={14} lineWidth={2}>
              {detail.history.map((entry, index) => (
                <Timeline.Item
                  key={`${index}-${entry.stateName}`}
                  title={entry.stateName}
                  color={jobStateColor(entry.stateName)}
                >
                  <Text size="xs" c="dimmed">
                    {formatDateTime(entry.createdAt) || '—'}
                  </Text>
                  {entry.reason && <Text size="xs">{entry.reason}</Text>}
                  {Object.entries(entry.data).map(([key, value]) => (
                    <Text key={key} size="xs" c="dimmed">
                      {key}: {value}
                    </Text>
                  ))}
                </Timeline.Item>
              ))}
            </Timeline>
          </>
        )}
      </Stack>
    </Drawer>
  );
};
